import { useContext, useEffect, useState } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';

import LocaleContext from 'components/context/Locale';
import Head from 'components/layout/Head';
import LayoutDefault from 'components/modules/public/layouts/Default';

import ForgottenPassword from './sections/ForgottenPassword';
import PasswordChanged from './sections/PasswordChanged';
import PasswordResetStatusAccepted from './sections/PasswordResetStatusAccepted';
import SetNewPassword from './sections/SetNewPassword';
import { Props } from './index';
import StyledComponent from './styles';

export enum Steps {
    PasswordReset = 'passwordReset',
    PasswordResetStatusAccepted = 'passwordResetStatusAccepted',
    PasswordSet = 'passwordSet',
    PasswordChanged = 'passwordChanged',
}

const PublicPagePasswordReset: NextPage<Props> = ({ state }) => {
    const router = useRouter();

    const localeContext = useContext(LocaleContext);
    const data = localeContext?.translations?.passwordReset;

    const [currentStep, setCurrentStep]: [string, Function] = useState(Steps.PasswordReset);
    const [didMount, setDidMount]: [boolean, Function] = useState(false);

    const getCurrentStep = () => {
        switch (currentStep) {
            case Steps.PasswordReset:
                return (
                    <ForgottenPassword
                        localeData={data.content.forgottenPassword}
                        onSuccess={() => setCurrentStep(Steps.PasswordResetStatusAccepted)}
                    />

                );
            case Steps.PasswordResetStatusAccepted:
                return (
                    <PasswordResetStatusAccepted
                        translations={data.content.PasswordResetStatusAccepted}
                    />
                );
            case Steps.PasswordSet:
                return (
                    <SetNewPassword
                        localeData={data.content.setNewPassword}
                        onSuccess={() => setCurrentStep(Steps.PasswordChanged)}
                    />
                );
            case Steps.PasswordChanged:
                return (
                    <PasswordChanged translations={data.content.passwordChanged} />
                );
            default:
                break;
        }
    };

    if (didMount && state?.authToken && currentStep === Steps.PasswordReset) router.push(Routes.PublicHome);

    useEffect(() => {
        setDidMount(true);

        if (didMount && router.query.token) setCurrentStep(Steps.PasswordSet);

        return () => setDidMount(false);
    }, [router.query.token]);

    if(!didMount) {
        return null;
    }

    return (
        <StyledComponent className="public-page-password-reset">
            <Head {...data.meta} />
            <LayoutDefault>
                {getCurrentStep()}
            </LayoutDefault>
        </StyledComponent>
    );
};

export default PublicPagePasswordReset;
